import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  X,
  Trash2,
  SkipForward,
  CheckCircle,
  FolderOpen,
  FileText,
  RotateCcw,
  ArrowUpToLine,
} from 'lucide-react'
import { useBatchDelete, BatchDeleteItem } from '../../contexts/BatchDeleteContext'
import styles from './BatchDeleteModal.module.css'

// ============================================================================
// Types
// ============================================================================

interface BatchDeleteModalProps {
  /** 删除笔记的回调函数 */
  onDeleteNote: (noteId: string) => Promise<void>
  /** 打开笔记的回调函数 - 用于批量删除时自动打开当前笔记 */
  onOpenNote?: (noteId: string) => void
  /** 移动笔记到根目录的回调函数 */
  onMoveToRoot?: (noteId: string) => Promise<void>
}

// ============================================================================
// Helper Components
// ============================================================================

/**
 * 层级指示器组件 - 显示笔记在层级结构中的深度
 */
const LevelIndicator: React.FC<{ level: number }> = ({ level }) => {
  if (level === 0) return null

  return (
    <div className={styles.levelIndicator}>
      {Array.from({ length: level }).map((_, i) => (
        <span key={i} className={styles.levelLine} />
      ))}
      <span className={styles.levelCorner} />
    </div>
  )
}

/**
 * 笔记项组件 - 显示单个笔记的信息
 */
const NoteItem: React.FC<{ item: BatchDeleteItem; isCurrent?: boolean }> = ({
  item,
  isCurrent = false,
}) => {
  return (
    <div
      className={`${styles.noteItem} ${isCurrent ? styles.noteItemCurrent : ''}`}
      style={{ paddingLeft: `${item.level * 20 + 16}px` }}
    >
      <LevelIndicator level={item.level} />
      <div className={styles.noteIcon}>
        {item.hasChildren ? (
          <FolderOpen className={styles.iconFolder} />
        ) : (
          <FileText className={styles.iconFile} />
        )}
      </div>
      <div className={styles.noteInfo}>
        <span className={styles.noteTitle}>{item.title || '无标题笔记'}</span>
        {item.hasChildren && (
          <span className={styles.noteMeta}>包含 {item.childrenCount} 个子笔记</span>
        )}
      </div>
    </div>
  )
}

/**
 * 进度条组件
 */
const ProgressBar: React.FC<{ percentage: number }> = ({ percentage }) => {
  return (
    <div className={styles.progressContainer}>
      <div className={styles.progressTrack}>
        <div
          className={styles.progressFill}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className={styles.progressText}>{percentage}%</span>
    </div>
  )
}

/**
 * 统计卡片组件
 */
const StatCard: React.FC<{
  icon: React.ReactNode
  label: string
  value: number
  variant: 'success' | 'warning' | 'default'
}> = ({ icon, label, value, variant }) => {
  const variantClass = {
    success: styles.statCardSuccess,
    warning: styles.statCardWarning,
    default: styles.statCardDefault,
  }[variant]

  return (
    <div className={`${styles.statCard} ${variantClass}`}>
      <div className={styles.statIcon}>{icon}</div>
      <div className={styles.statInfo}>
        <span className={styles.statValue}>{value}</span>
        <span className={styles.statLabel}>{label}</span>
      </div>
    </div>
  )
}

// ============================================================================
// Status Views
// ============================================================================

/**
 * 确认阶段视图 - 显示待删除笔记列表
 */
const ConfirmingView: React.FC<{
  items: BatchDeleteItem[]
  onStart: () => void
  onCancel: () => void
}> = ({ items, onStart, onCancel }) => {
  const totalCount = items.length
  // 由于删除顺序是"先子后父"，最高层级父笔记是数组最后一个元素
  const rootNote = items[items.length - 1]

  return (
    <div className={`${styles.viewContent} ${styles.viewEnter}`}>
      <div className={styles.alertBox}>        
        <div className={styles.alertContent}>
          <h4 className={styles.alertTitle}>确认批量删除（移入回收站）</h4>
          <p className={styles.alertText}>
            您即将将笔记 <strong>{rootNote?.title || '该笔记'}</strong> 及其所有子笔记移入回收站。
          </p>
        </div>
      </div>

      <div className={styles.summaryBox}>
        <div className={styles.summaryHeader}>
          <span className={styles.summaryTitle}>待删除笔记</span>
          <span className={styles.summaryCount}>共 {totalCount} 个</span>
        </div>
        <div className={styles.noteList}>
          {items.slice(0, 5).map((item) => (
            <NoteItem key={item.id} item={item} />
          ))}
          {totalCount > 5 && (
            <div className={styles.moreIndicator}>
              还有 {totalCount - 5} 个笔记...
            </div>
          )}
        </div>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={`${styles.button} ${styles.buttonSecondary}`}
        >
          取消
        </button>
        <button
          type="button"
          onClick={onStart}
          className={`${styles.button} ${styles.buttonDanger}`}
        >
          <Trash2 className={styles.buttonIcon} />
          开始删除
        </button>
      </div>
    </div>
  )
}

/**
 * 处理阶段视图 - 显示当前处理进度
 */
const ProcessingView: React.FC<{
  currentItem: BatchDeleteItem | null
  progress: { current: number; total: number; percentage: number }
  onConfirm: () => void
  onMoveToRoot: () => void
  onCancel: () => void
  onOpenNote?: (noteId: string) => void
}> = ({ currentItem, progress, onConfirm, onMoveToRoot, onCancel, onOpenNote }) => {
  const [openError, setOpenError] = useState<string | null>(null)
  const prevItemIdRef = useRef<string | null>(null)

  // 当当前项目变化时自动打开笔记
  useEffect(() => {
    if (currentItem && onOpenNote && currentItem.id !== prevItemIdRef.current) {
      try {
        onOpenNote(currentItem.id)
        setOpenError(null)
      } catch (error) {
        console.error('打开笔记失败:', error)
        setOpenError('打开笔记失败，但批量删除将继续')
      }
      prevItemIdRef.current = currentItem.id
    }
  }, [currentItem, onOpenNote])

  if (!currentItem) return null

  return (
    <div className={`${styles.viewContent} ${styles.viewEnter}`}>
      <div className={styles.progressSection}>
        <div className={styles.progressHeader}>
          <span className={styles.progressLabel}>处理进度</span>
          <span className={styles.progressCount}>
            {progress.current} / {progress.total}
          </span>
        </div>
        <ProgressBar percentage={progress.percentage} />
      </div>

      <div className={styles.currentItemBox}>
        <div className={styles.currentItemHeader}>
          <span className={styles.currentItemLabel}>当前笔记</span>
          {currentItem.hasChildren && (
            <span className={styles.childrenBadge}>
              {currentItem.childrenCount} 个子笔记
            </span>
          )}
        </div>
        <NoteItem item={currentItem} isCurrent />
        {openError && (
          <div className={styles.openErrorHint}>
            <AlertTriangle className={styles.warningIcon} />
            <span>{openError}</span>
          </div>
        )}
      </div>

      <div className={styles.warningHint}>
        <AlertTriangle className={styles.warningIcon} />
        <span>确认删除此笔记？此操作不可撤销。</span>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onCancel}
          className={`${styles.button} ${styles.buttonSecondary}`}
        >
          取消全部
        </button>
        <button
          type="button"
          onClick={onMoveToRoot}
          className={`${styles.button} ${styles.buttonTertiary}`}
          title="将此笔记移动到根目录，保留笔记内容"
        >
          <ArrowUpToLine className={styles.buttonIcon} />
          移到根目录
        </button>
        <button
          type="button"
          onClick={onConfirm}
          className={`${styles.button} ${styles.buttonDanger}`}
        >
          <Trash2 className={styles.buttonIcon} />
          确认删除
        </button>
      </div>
    </div>
  )
}

/**
 * 完成阶段视图 - 显示处理结果统计
 */
const CompletedView: React.FC<{
  results: { deleted: string[]; skipped: string[]; movedToRoot: string[] }
  total: number
  onClose: () => void
}> = ({ results, total, onClose }) => {
  const deletedCount = results.deleted.length
  const movedToRootCount = results.movedToRoot.length

  return (
    <div className={`${styles.viewContent} ${styles.viewEnter}`}>
      <div className={styles.successHeader}>
        <div className={styles.successIcon}>
          <CheckCircle className={styles.checkIcon} />
        </div>
        <h4 className={styles.successTitle}>批量删除完成</h4>
        <p className={styles.successText}>
          已成功处理 {total} 个笔记
        </p>
      </div>

      <div className={styles.statsGrid}>
        <StatCard
          icon={<Trash2 className={styles.statIconInner} />}
          label="已移入回收站"
          value={deletedCount}
          variant="success"
        />
        <StatCard
          icon={<ArrowUpToLine className={styles.statIconInner} />}
          label="移到根目录"
          value={movedToRootCount}
          variant="warning"
        />
      </div>

      {movedToRootCount > 0 && (
        <div className={styles.skippedHint}>
          <AlertTriangle className={styles.skippedIcon} />
          <span>有 {movedToRootCount} 个笔记被移动到根目录保留。</span>
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onClose}
          className={`${styles.button} ${styles.buttonPrimary}`}
        >
          完成
        </button>
      </div>
    </div>
  )
}

/**
 * 取消阶段视图 - 显示部分处理结果
 */
const CancelledView: React.FC<{
  results: { deleted: string[]; skipped: string[]; movedToRoot: string[] }
  total: number
  onClose: () => void
}> = ({ results, total, onClose }) => {
  const deletedCount = results.deleted.length
  const movedToRootCount = results.movedToRoot.length
  const processedCount = deletedCount + movedToRootCount

  return (
    <div className={`${styles.viewContent} ${styles.viewEnter}`}>
      <div className={styles.cancelledHeader}>
        <div className={styles.cancelledIcon}>
          <RotateCcw className={styles.cancelIcon} />
        </div>
        <h4 className={styles.cancelledTitle}>操作已取消</h4>
        <p className={styles.cancelledText}>
          已处理 {processedCount} / {total} 个笔记
        </p>
      </div>

      <div className={styles.statsGrid}>
        <StatCard
          icon={<Trash2 className={styles.statIconInner} />}
          label="已移入回收站"
          value={deletedCount}
          variant="success"
        />
        <StatCard
          icon={<ArrowUpToLine className={styles.statIconInner} />}
          label="移到根目录"
          value={movedToRootCount}
          variant="warning"
        />
      </div>

      <div className={styles.cancelledHint}>
        <AlertTriangle className={styles.cancelledHintIcon} />
        <span>操作已中断，未处理的笔记保持不变。</span>
      </div>

      <div className={styles.actions}>
        <button
          type="button"
          onClick={onClose}
          className={`${styles.button} ${styles.buttonPrimary}`}
        >
          关闭
        </button>
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export const BatchDeleteModal: React.FC<BatchDeleteModalProps> = memo(
  function BatchDeleteModal({ onDeleteNote, onOpenNote, onMoveToRoot }) {
    const {
      isOpen,
      items,
      status,
      results,
      confirmDelete,
      moveToRoot,
      markDeleted,
      cancelBatch,
      processNext,
      getCurrentItem,
      getProgress,
    } = useBatchDelete()

    const contentRef = useRef<HTMLDivElement>(null)
    const [isClosing, setIsClosing] = useState(false)
    const currentItem = getCurrentItem()
    const progress = getProgress()

    // 处理确认删除
    const handleConfirmDelete = useCallback(async () => {
      if (!currentItem) return

      try {
        await onDeleteNote(currentItem.id)
        markDeleted(currentItem.id)
        processNext()
      } catch (error) {
        console.error('删除笔记失败:', error)
        // 即使失败也继续处理下一个
        processNext()
      }
    }, [currentItem, onDeleteNote, markDeleted, processNext])

    // 处理移到根目录
    const handleMoveToRoot = useCallback(async () => {
      if (!currentItem) return

      try {
        if (onMoveToRoot) {
          await onMoveToRoot(currentItem.id)
        }
        moveToRoot()
      } catch (error) {
        console.error('移动笔记到根目录失败:', error)
        // 即使失败也继续处理下一个
        moveToRoot()
      }
    }, [currentItem, onMoveToRoot, moveToRoot])

    // 处理关闭
    const handleClose = useCallback(() => {
      setIsClosing(true)
      setTimeout(() => {
        cancelBatch()
        setIsClosing(false)
      }, 200)
    }, [cancelBatch])

    // ESC 键关闭
    useEffect(() => {
      if (!isOpen) return

      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          handleClose()
        }
      }

      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'

      return () => {
        document.removeEventListener('keydown', handleEscape)
        document.body.style.overflow = ''
      }
    }, [isOpen, handleClose])

    // 点击背景关闭
    const handleBackdropClick = useCallback(
      (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
          handleClose()
        }
      },
      [handleClose]
    )

    if (!isOpen) return null

    const overlayClass = `${styles.overlay} ${isClosing ? styles.overlayExit : styles.overlayEnter}`
    const contentClass = `${styles.content} ${isClosing ? styles.contentExit : styles.contentEnter}`

    return (
      <div
        className={overlayClass}
        onClick={handleBackdropClick}
        role="dialog"
        aria-modal="true"
        aria-labelledby="batch-delete-title"
      >
        <div
          ref={contentRef}
          className={contentClass}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              <div className={`${styles.headerIcon} ${styles[status]}`}>
                {status === 'confirming' && <AlertTriangle className={styles.icon} />}
                {status === 'processing' && <Trash2 className={styles.icon} />}
                {status === 'completed' && <CheckCircle className={styles.icon} />}
                {status === 'cancelled' && <RotateCcw className={styles.icon} />}
              </div>
              <h2 id="batch-delete-title" className={styles.title}>
                {status === 'confirming' && '批量删除确认'}
                {status === 'processing' && '正在删除...'}
                {status === 'completed' && '删除完成'}
                {status === 'cancelled' && '已取消'}
              </h2>
            </div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleClose}
              aria-label="关闭"
            >
              <X className={styles.closeIcon} />
            </button>
          </div>

          {/* Content */}
          <div className={styles.body}>
            {status === 'confirming' && (
              <ConfirmingView
                items={items}
                onStart={confirmDelete}
                onCancel={handleClose}
              />
            )}

            {status === 'processing' && (
              <ProcessingView
                currentItem={currentItem}
                progress={progress}
                onConfirm={handleConfirmDelete}
                onMoveToRoot={handleMoveToRoot}
                onCancel={handleClose}
                onOpenNote={onOpenNote}
              />
            )}

            {status === 'completed' && (
              <CompletedView
                results={results}
                total={items.length}
                onClose={handleClose}
              />
            )}

            {status === 'cancelled' && (
              <CancelledView
                results={results}
                total={items.length}
                onClose={handleClose}
              />
            )}
          </div>
        </div>
      </div>
    )
  }
)

export default BatchDeleteModal
